import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useNavigate,
} from 'react-router';
import {
  // find,
  get,
} from 'lodash';
import {
  API,
  NavigationHelper,
  ToastActions,
  SidePanelContainer,
  SidePanelContent,
  SidePanelFooter,
  Field2 as Field,
  VibeModal,
  VibeButton,
  VibeIcon,
  viArchive,
  viUnarchive,
  color,
} from 'vibeguide';

function ZoneSidebar({
  className = '',
  style = {},
  zoneId = '',
  user,
  queueToast,
}) {
  const [zone, setZone] = useState({});
  const [confirmArchive, setConfirmArchive] = useState(false);

  const history = useNavigate();

  const showArchive = zone._id && user.can('location-zone.delete');
  const isArchived = zone._id && !zone.active;

  const disableSave = !zone.name;
  const disableInput = (zone._id && !user.can('location-zone.modify'))
    || (!zone._id && !user.can('location-zone.create'));

  /**
   * User clicks Archive
   */
  const onClickArchive = () => {
    setConfirmArchive(true);
  };

  /**
   * User clicks Unarchive
   */
  const onClickUnarchive = async () => {
    await API.LocationZones.reactivate({
      _id: zone._id,
    });

    setZone({
      ...zone,
      active: true,
    });

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveZone'));
  };

  /**
   * User confirms archiving the category
   */
  const onConfirmArchive = async () => {
    setConfirmArchive(false);
    await API.LocationZones.deactivate(zone._id);

    setZone({
      ...zone,
      active: false,
    });

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveZone'));
  };

  /**
   * User cancels archiving the category
   */
  const onCancelArchive = () => {
    setConfirmArchive(false);
  };

  /**
   * User changes the input box
   */
  const onChange = ({
    target: {
      name,
      value,
    },
  }) => {
    setZone({
      ...zone,
      [name]: value,
    });
  };

  /**
   * Save the category
   */
  const onSave = async () => {
    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    const data = {
      name: zone.name,
    };

    if (zone._id) {
      data._id = zone._id;
    }

    try {
      const response = zone._id
        ? await API.LocationZones.update(data)
        : await API.LocationZones.create(data);

      const zoneId = get(response, '[0].documentId', null);
      const responseType = get(response, '[0].type');

      const success = zone._id
        ? responseType === 'LOCATIONZONE.UPDATED'
        : responseType === 'LOCATIONZONE.CREATED';

      if (success) {
        // Successfully saved the baseline
        const saveMessage = zone._id
          ? 'Zone Updated!'
          : 'Zone Created!';

        queueToast({
          type: 'success',
          title: saveMessage,
          allowClose: true,
          delay: 500,
        });
      }

      // Refresh the user details and redirect to their edit screen
      if (success && !zone._id) {
        const redirectUrl = NavigationHelper.updateParams({
          zoneId,
          type: null,
        });

        history(redirectUrl);
      }

      document.dispatchEvent(new Event('onSaveZone'));
    } catch (err) {
      console.error('Error saving zone', err);

      queueToast({
        type: 'error',
        title: 'Error Saving Zone',
        timeout: 10,
        allowClose: true,
      });
    }
  };

  /**
   * Get the zone from the API
   */
  const getZone = async () => {
    const zone = await API.LocationZones.getById(zoneId);

    setZone(zone || {});
  };

  useEffect(() => {
    if (zoneId) {
      getZone();
    }
  }, [zoneId]);

  return (
    <SidePanelContainer
      className={classNames('ZoneSidebar', className)}
      style={style}
    >
      <SidePanelContent>
        <div>
          <Field
            type="text"
            label="Zone"
            placeholder="Zone"
            name="name"
            value={zone.name}
            tabIndex={1}
            disabled={disableInput}
            testId="location-zone-name-input"
            onChange={onChange}
            required
            autoFocus
          />
        </div>
      </SidePanelContent>

      <SidePanelFooter>
        <VibeButton
          text="Save Changes"
          color="primary"
          disabled={disableSave || disableInput}
          testId="location-zone-save-button"
          onClick={onSave}
        />

        {zone._id && (
          <div>
            {showArchive && !isArchived && (
              <VibeIcon
                icon={viArchive}
                type="button"
                buttonProps={{
                  size: 32,
                  borderColor: color.error,
                  style: {
                    marginLeft: 8,
                  },
                }}
                tooltip="Archive"
                color={color.error}
                size={20}
                onClick={onClickArchive}
                data-testid="location-zone-archive-button"
              />
            )}

            {showArchive && isArchived && (
              <VibeIcon
                icon={viUnarchive}
                type="button"
                buttonProps={{
                  size: 32,
                  borderColor: color.success,
                  style: {
                    marginLeft: 8,
                  },
                }}
                tooltip="Unarchive"
                color={color.success}
                size={20}
                onClick={onClickUnarchive}
                data-testid="location-zone-unarchive-button"
              />
            )}
          </div>
        )}
      </SidePanelFooter>

      <VibeModal
        show={confirmArchive}
        type="confirm"
        title="Archive Location Zone"
        text={`Are you sure you want to archive ${zone.name}?`}
        confirmProps={{
          color: 'error',
        }}
        testIds={{
          root: 'archive-location-zone-dialog',
          confirm: 'archive-location-zone-confirm-button',
          cancel: 'archive-location-zone-cancel-button',
        }}
        onConfirm={onConfirmArchive}
        onClose={onCancelArchive}
      />
    </SidePanelContainer>
  );
}

ZoneSidebar.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  zoneId: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  queueToast: ToastActions.queueToast,
};

export default connect(mapStateToProps, mapDispatchToProps)(ZoneSidebar);
